import { useState } from "react";
import { api, ApiError } from "../api.ts";
import { ErrorBanner } from "../ErrorBanner.tsx";
import { Modal } from "../Modal.tsx";
import {
  SAFETY_NOTE_CATEGORIES,
  type SafetyNote,
  type SafetyNoteCategory,
} from "../types.ts";

interface Props {
  communityId: string;
  initial: SafetyNote | null;
  onClose: () => void;
  onSaved: () => void;
}

interface CategoryFieldSpec {
  key: string;
  label: string;
  hint?: string;
  type?: "text" | "textarea";
}

const CATEGORY_LABELS: Record<SafetyNoteCategory, string> = {
  scam_pattern: "Scam pattern",
  transport_hazard: "Transport hazard",
  water_hazard: "Water hazard",
  general: "General",
};

const STRUCTURED_FIELDS: Record<SafetyNoteCategory, CategoryFieldSpec[]> = {
  scam_pattern: [
    {
      key: "tactic",
      label: "Tactic",
      hint: "Describe the pattern, not the people. (No naming individuals.)",
      type: "textarea",
    },
    { key: "common_target", label: "Common target (e.g. tourists, ATMs)" },
    { key: "time_of_day", label: "Time of day" },
    { key: "recommended_action", label: "Recommended action", type: "textarea" },
  ],
  transport_hazard: [
    { key: "route_or_road", label: "Route or road" },
    { key: "hazard_type", label: "Hazard type" },
    { key: "time_of_day", label: "Time of day" },
    {
      key: "recommended_action",
      label: "Recommended action",
      type: "textarea",
    },
  ],
  water_hazard: [
    { key: "water_body", label: "Water body (beach, river…)" },
    { key: "hazard_type", label: "Hazard type (rip tide, contamination…)" },
    { key: "season", label: "Season / conditions" },
    {
      key: "recommended_action",
      label: "Recommended action",
      type: "textarea",
    },
  ],
  general: [
    { key: "context", label: "Context", type: "textarea" },
    {
      key: "recommended_action",
      label: "Recommended action",
      type: "textarea",
    },
  ],
};

const FORBIDDEN_KEYS = new Set([
  "named_individual",
  "named_individuals",
  "individual",
  "individuals",
  "individual_name",
  "person",
  "person_name",
  "persons",
  "people_named",
  "accused",
  "accused_name",
  "accused_individual",
  "target_name",
  "target_individual",
  "subject_name",
  "full_name",
  "first_name",
  "last_name",
]);

function structuredToStrings(
  s: Record<string, unknown>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(s ?? {})) {
    if (typeof v === "string") out[k] = v;
    else if (v == null) out[k] = "";
    else out[k] = String(v);
  }
  return out;
}

export function SafetyNoteForm({
  communityId,
  initial,
  onClose,
  onSaved,
}: Props) {
  const isEdit = initial !== null;
  const [category, setCategory] = useState<SafetyNoteCategory>(
    initial?.category ?? "scam_pattern",
  );
  const [patternTitle, setPatternTitle] = useState(initial?.patternTitle ?? "");
  const [patternDescription, setPatternDescription] = useState(
    initial?.patternDescription ?? "",
  );
  const [placeDescription, setPlaceDescription] = useState(
    initial?.placeDescription ?? "",
  );
  const [structured, setStructured] = useState<Record<string, string>>(
    initial ? structuredToStrings(initial.structured) : {},
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function setField(key: string, value: string) {
    setStructured((prev) => ({ ...prev, [key]: value }));
  }

  function changeCategory(c: SafetyNoteCategory) {
    setCategory(c);
    setStructured({});
  }

  async function save() {
    setError(null);
    setBusy(true);
    try {
      const trimmedTitle = patternTitle.trim();
      const trimmedDesc = patternDescription.trim();
      if (!trimmedTitle) {
        setError("pattern_title_required");
        setBusy(false);
        return;
      }
      if (!trimmedDesc) {
        setError("pattern_description_required");
        setBusy(false);
        return;
      }
      const structuredPayload: Record<string, string> = {};
      for (const [k, v] of Object.entries(structured)) {
        if (FORBIDDEN_KEYS.has(k.toLowerCase())) {
          setError(`named_individual_field_forbidden: ${k}`);
          setBusy(false);
          return;
        }
        const trimmed = v.trim();
        if (trimmed) structuredPayload[k] = trimmed;
      }
      if (isEdit) {
        await api(`/admin/safety-notes/${encodeURIComponent(initial!.id)}`, {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            pattern_title: trimmedTitle,
            pattern_description: trimmedDesc,
            place_description: placeDescription,
            structured: structuredPayload,
          }),
        });
      } else {
        await api(`/admin/safety-notes`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            community_id: communityId,
            category,
            pattern_title: trimmedTitle,
            pattern_description: trimmedDesc,
            place_description: placeDescription,
            structured: structuredPayload,
          }),
        });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const fields = STRUCTURED_FIELDS[category];

  return (
    <Modal
      title={isEdit ? `Edit safety note #${initial!.id}` : "Add safety note"}
      subtitle={
        isEdit
          ? `community #${initial!.communityId} · ${CATEGORY_LABELS[initial!.category]}`
          : `community #${communityId}`
      }
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="luna-btn luna-btn-ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="luna-btn luna-btn-primary"
            onClick={() => void save()}
            disabled={busy}
          >
            {busy
              ? isEdit
                ? "Saving…"
                : "Creating…"
              : isEdit
                ? "Save changes"
                : "Create safety note"}
          </button>
        </>
      }
    >
      {error && <ErrorBanner code={error} />}
      <div className="community-required">
        Describe patterns and places, never named individuals. Fields like
        full_name / accused / person are rejected by the server.
      </div>
      <div className="field">
        <label htmlFor="n-category">Category</label>
        <select
          id="n-category"
          className="luna-input"
          value={category}
          onChange={(e) =>
            changeCategory(e.target.value as SafetyNoteCategory)
          }
          disabled={isEdit}
        >
          {SAFETY_NOTE_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABELS[c]}
            </option>
          ))}
        </select>
        {isEdit && (
          <span className="hint">Category is fixed after creation.</span>
        )}
      </div>
      <div className="field">
        <label htmlFor="n-pattern-title">Pattern title</label>
        <input
          id="n-pattern-title"
          className="luna-input"
          value={patternTitle}
          onChange={(e) => setPatternTitle(e.target.value)}
          placeholder="e.g. Skimmer on the standalone ATM"
        />
      </div>
      <div className="field">
        <label htmlFor="n-pattern-desc">Pattern description</label>
        <textarea
          id="n-pattern-desc"
          className="luna-input"
          value={patternDescription}
          onChange={(e) => setPatternDescription(e.target.value)}
          rows={4}
          placeholder="What the pattern looks like. Describe behavior, not people."
        />
      </div>
      <div className="field">
        <label htmlFor="n-place">Place description</label>
        <input
          id="n-place"
          className="luna-input"
          value={placeDescription}
          onChange={(e) => setPlaceDescription(e.target.value)}
          placeholder="e.g. Avenue 5 at Calle 14, standalone ATM"
        />
      </div>
      <div className="field">
        <label>Structured fields ({CATEGORY_LABELS[category]})</label>
        {fields.map((f) =>
          f.type === "textarea" ? (
            <div key={f.key} className="field" style={{ marginTop: 8 }}>
              <label htmlFor={`n-s-${f.key}`}>{f.label}</label>
              <textarea
                id={`n-s-${f.key}`}
                className="luna-input"
                value={structured[f.key] ?? ""}
                onChange={(e) => setField(f.key, e.target.value)}
                rows={3}
              />
              {f.hint && <span className="hint">{f.hint}</span>}
            </div>
          ) : (
            <div key={f.key} className="field" style={{ marginTop: 8 }}>
              <label htmlFor={`n-s-${f.key}`}>{f.label}</label>
              <input
                id={`n-s-${f.key}`}
                className="luna-input"
                value={structured[f.key] ?? ""}
                onChange={(e) => setField(f.key, e.target.value)}
              />
              {f.hint && <span className="hint">{f.hint}</span>}
            </div>
          ),
        )}
      </div>
    </Modal>
  );
}
